import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Play, Pause, RotateCcw, Save } from "lucide-react";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import TeleprompterLyrics from "./TeleprompterLyrics";
import TeleprompterControls from "./TeleprompterControls";
import TeleprompterCountdown from "./TeleprompterCountdown";
import { useSectionEditor, SectionEditorLyrics, SectionEditorPanel } from "./SectionEditor";
import { SECTION_COLORS } from "./sectionColors";

const TeleprompterView = ({
  song,
  isOpen,
  onClose,
  isMobile = false,
  // Recording props
  recorder = null,
  isPro = false,
  // Teleprompter hook instance
  teleprompter,
  // Save take callback
  onSaveTake = null,
}) => {
  const wakeLockRef = useRef(null);

  // Waveform refs and state
  const beatWsContainerRef = useRef(null);
  const beatWsRef = useRef(null);
  const readOnlyRegionsRef = useRef(null);
  const [beatDuration, setBeatDuration] = useState(0);

  // Fullscreen (mobile only) + Wake Lock
  useEffect(() => {
    if (isOpen) {
      if (isMobile) {
        document.documentElement.requestFullscreen?.().catch(() => {});
      }
      navigator.wakeLock
        ?.request("screen")
        .then((lock) => {
          wakeLockRef.current = lock;
        })
        .catch(() => {});
    } else {
      if (isMobile) {
        document.exitFullscreen?.().catch(() => {});
      }
      wakeLockRef.current?.release().catch(() => {});
    }
    return () => {
      if (isMobile) {
        document.exitFullscreen?.().catch(() => {});
      }
      wakeLockRef.current?.release().catch(() => {});
    };
  }, [isOpen, isMobile]);

  // Clear stale recorder state when teleprompter opens
  useEffect(() => {
    if (isOpen && recorder?.audioBlob && !recorder.isRecording) {
      recorder.resetRecording();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Beat WaveSurfer initialization
  useEffect(() => {
    if (!isOpen || !song?.audioFileUrl || !beatWsContainerRef.current) return;

    const ws = WaveSurfer.create({
      container: beatWsContainerRef.current,
      height: 24,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      waveColor: "#d1d5db",
      progressColor: "#6b7280",
      cursorColor: "#9ca3af",
      normalize: true,
      interact: true,
      backend: "WebAudio",
      fillParent: true,
    });

    ws.on("ready", () => {
      setBeatDuration(ws.getDuration());
      teleprompter.setBeatWaveSurfer(ws);
    });

    ws.load(song.audioFileUrl);
    beatWsRef.current = ws;

    return () => {
      ws.destroy();
      beatWsRef.current = null;
      teleprompter.setBeatWaveSurfer(null);
      setBeatDuration(0);
    };
  }, [isOpen, song?.audioFileUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close section editor when teleprompter closes
  useEffect(() => {
    if (!isOpen && teleprompter.showSectionEditor) {
      teleprompter.setShowSectionEditor(false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show read-only section regions on waveform when NOT in editor mode
  useEffect(() => {
    const ws = beatWsRef.current;
    const sections = teleprompter.sections;
    const editorOpen = teleprompter.showSectionEditor;

    // Clean up any existing read-only regions
    if (readOnlyRegionsRef.current) {
      readOnlyRegionsRef.current.clearRegions();
      readOnlyRegionsRef.current.destroy();
      readOnlyRegionsRef.current = null;
    }

    // Only show when: WaveSurfer is ready, sections exist, editor is NOT open
    if (!ws || !sections?.length || editorOpen || !beatDuration) return;

    const regions = ws.registerPlugin(RegionsPlugin.create());
    readOnlyRegionsRef.current = regions;

    sections.forEach((section, idx) => {
      const color = section.color || SECTION_COLORS[idx % SECTION_COLORS.length];
      regions.addRegion({
        id: `readonly-${section.id}`,
        start: section.audioStartSec,
        end: section.audioEndSec,
        color,
        drag: false,
        resize: false,
      });
    });

    return () => {
      if (readOnlyRegionsRef.current) {
        readOnlyRegionsRef.current.clearRegions();
        readOnlyRegionsRef.current.destroy();
        readOnlyRegionsRef.current = null;
      }
    };
  }, [teleprompter.sections, teleprompter.showSectionEditor, beatDuration]);

  // Save take modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const previewAudioRef = useRef(null);
  const wasRecordingRef = useRef(false);

  // Track when recording starts inside the teleprompter
  useEffect(() => {
    if (recorder?.isRecording) {
      wasRecordingRef.current = true;
    }
  }, [recorder?.isRecording]);

  // Reset tracking when teleprompter closes
  useEffect(() => {
    if (!isOpen) {
      wasRecordingRef.current = false;
      setShowSaveModal(false);
    }
  }, [isOpen]);

  // Show save modal only when a recording finishes inside the teleprompter
  useEffect(() => {
    if (
      wasRecordingRef.current &&
      recorder?.audioBlob &&
      !recorder.isRecording
    ) {
      wasRecordingRef.current = false;
      const url = URL.createObjectURL(recorder.audioBlob);
      setPreviewUrl(url);
      setSaveLabel("");
      setShowSaveModal(true);
      return () => URL.revokeObjectURL(url);
    }
  }, [recorder?.audioBlob, recorder?.isRecording]);

  const handleSaveModalSave = async () => {
    if (!recorder?.audioBlob || !onSaveTake || !song) return;
    setIsSaving(true);
    try {
      await onSaveTake(
        song.id,
        recorder.audioBlob,
        saveLabel || undefined,
        recorder.recordingDuration,
      );
      setShowSaveModal(false);
      recorder.resetRecording();
    } catch (err) {
      console.error("❌ Error saving take:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveModalDiscard = () => {
    setShowSaveModal(false);
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    setIsPreviewPlaying(false);
    recorder?.resetRecording();
  };

  const handleSaveModalReRecord = () => {
    setShowSaveModal(false);
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    setIsPreviewPlaying(false);
    recorder?.resetRecording();
    recorder?.startRecording();
    if (!teleprompter.isPlaying) {
      teleprompter.startPlayback();
    }
  };

  const togglePreview = () => {
    if (!previewAudioRef.current) return;
    if (isPreviewPlaying) {
      previewAudioRef.current.pause();
      setIsPreviewPlaying(false);
    } else {
      previewAudioRef.current.play();
      setIsPreviewPlaying(true);
    }
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Section editor handlers
  const handleSaveSections = useCallback(
    (newSections) => {
      teleprompter.saveSectionConfig(newSections);
      teleprompter.setShowSectionEditor(false);
    },
    [teleprompter],
  );

  const handleCancelSectionEditor = useCallback(() => {
    teleprompter.setShowSectionEditor(false);
  }, [teleprompter]);

  // Keyboard controls
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      // Don't handle keys when save modal or section editor is open
      if (showSaveModal || teleprompter.showSectionEditor) return;

      switch (e.key) {
        case " ":
          e.preventDefault();
          if (teleprompter.isPlaying) {
            teleprompter.pausePlayback();
          } else {
            teleprompter.startPlayback();
          }
          break;
        case "Escape":
          onClose();
          break;
        case "ArrowUp":
          e.preventDefault();
          if (!teleprompter.sections?.length) {
            teleprompter.setScrollSpeed((s) => Math.min(s + 10, 300));
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          if (!teleprompter.sections?.length) {
            teleprompter.setScrollSpeed((s) => Math.max(s - 10, 30));
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, teleprompter, onClose, showSaveModal]);

  // Section editor hook — always called to obey React hooks rules
  const sectionEditor = useSectionEditor({
    active: isOpen && !!song && teleprompter.showSectionEditor,
    song,
    beatWaveSurfer: beatWsRef.current,
    initialSections: teleprompter.sections || [],
    onSave: handleSaveSections,
    onCancel: handleCancelSectionEditor,
    isMobile,
    beatDuration,
  });

  if (!isOpen || !song) return null;

  const showingEditor = teleprompter.showSectionEditor;

  // Shared inner content
  const headerBar = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 16px",
        borderBottom: "1px solid rgba(74, 92, 74, 0.5)",
        flexShrink: 0,
      }}
      className="bg-forest-900/80 backdrop-blur-sm"
    >
      <h2
        className="text-forest-200 text-sm truncate"
        style={{ maxWidth: "70%" }}
      >
        {song.title}
        {showingEditor && (
          <span className="ml-2 text-xs text-gold-500 font-normal">
            — Section Editor
          </span>
        )}
      </h2>
      <button
        onClick={showingEditor ? handleCancelSectionEditor : onClose}
        className="p-1.5 rounded transition-colors hover:bg-forest-700 text-forest-400 hover:text-forest-200"
        title={showingEditor ? "Close section editor" : "Close teleprompter (Esc)"}
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );

  const countdownOverlay = teleprompter.showCountdown ? (
    <TeleprompterCountdown
      seconds={teleprompter.countdownSeconds}
      onComplete={teleprompter.onCountdownComplete}
    />
  ) : null;

  const lyrics = showingEditor ? null : (
    <TeleprompterLyrics
      lines={teleprompter.lines}
      currentLineIndex={teleprompter.currentLineIndex}
      fontSize={teleprompter.fontSize}
    />
  );

  const waveformStrip = (
    <div
      className="px-4 py-2 bg-forest-900/60 border-t border-forest-700/50"
      style={{ flexShrink: 0 }}
    >
      {song?.audioFileUrl && (
        <div>
          <div
            ref={beatWsContainerRef}
            style={{
              height: "24px",
              width: "100%",
              backgroundColor: "#2e3e3a",
              border: "1px solid #4a6057",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          />
        </div>
      )}
    </div>
  );

  const saveModal = showSaveModal && previewUrl && (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 65,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.7)",
      }}
    >
      <div
        className="bg-forest-900 border border-forest-600 rounded-lg shadow-2xl"
        style={{ width: "320px", padding: "20px" }}
      >
        <h3 className="text-forest-200 text-sm font-semibold mb-3">
          Save Recording
        </h3>

        {/* Preview playback */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={togglePreview}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-forest-700 hover:bg-forest-600 text-gold-500"
          >
            {isPreviewPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </button>
          <span className="text-sm text-forest-200 font-mono">
            {formatTime(recorder?.recordingDuration || 0)}
          </span>
          <audio
            ref={previewAudioRef}
            src={previewUrl}
            onEnded={() => setIsPreviewPlaying(false)}
          />
        </div>

        {/* Label input */}
        <input
          type="text"
          value={saveLabel}
          onChange={(e) => setSaveLabel(e.target.value)}
          placeholder="Take name (optional)"
          className="w-full mb-3 px-2 py-1.5 text-sm bg-forest-800 border border-forest-600 rounded text-forest-200 placeholder-forest-500 focus:outline-none focus:border-gold-500"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSaveModalSave();
            e.stopPropagation();
          }}
        />

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveModalDiscard}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-forest-700 hover:bg-forest-600 text-forest-400 rounded transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Discard
          </button>
          <button
            onClick={handleSaveModalReRecord}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-forest-700 hover:bg-forest-600 text-forest-200 rounded transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Re-record
          </button>
          <button
            onClick={handleSaveModalSave}
            disabled={isSaving}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-forest-700 hover:bg-forest-600 text-gold-500 rounded transition-colors border border-gold-500/30"
          >
            <Save className="w-3.5 h-3.5" />
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );

  const controls = showingEditor ? null : (
    <div style={{ flexShrink: 0 }}>
      <TeleprompterControls
        teleprompter={teleprompter}
        recorder={recorder}
        isMobile={isMobile}
        isPro={isPro}
        beatDuration={beatDuration}
        song={song}
      />
    </div>
  );


  // Mobile: fullscreen overlay — extra bottom padding for system nav bar
  if (isMobile) {
    return createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 60,
          display: "flex",
          flexDirection: "column",
          paddingTop: "env(safe-area-inset-top, 24px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        className="bg-forest-950"
      >
        {headerBar}
        {countdownOverlay}
        {showingEditor ? <SectionEditorLyrics editor={sectionEditor} /> : lyrics}
        {waveformStrip}
        {showingEditor ? (
          <div style={{ flexShrink: 0, paddingBottom: "72px" }}>
            <SectionEditorPanel editor={sectionEditor} />
          </div>
        ) : (
          <div style={{ flexShrink: 0, paddingBottom: "72px" }}>
            <TeleprompterControls
              teleprompter={teleprompter}
              recorder={recorder}
              isMobile={isMobile}
              isPro={isPro}
              beatDuration={beatDuration}
              song={song}
            />
          </div>
        )}
        {saveModal}
      </div>,
      document.body,
    );
  }

  // Desktop: centered panel matching song card width (max-w-6xl = 72rem)
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.6)",
        }}
        onClick={showingEditor ? undefined : onClose}
      />
      {/* Centered panel */}
      <div
        style={{
          position: "absolute",
          top: "5rem",
          bottom: "2rem",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: "72rem",
          padding: "0 1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid rgba(197, 163, 78, 0.4)",
          }}
          className="bg-forest-950 shadow-2xl"
        >
          {headerBar}
          {countdownOverlay}
          {showingEditor ? <SectionEditorLyrics editor={sectionEditor} /> : lyrics}
          {waveformStrip}
          {showingEditor ? <SectionEditorPanel editor={sectionEditor} /> : controls}
          {saveModal}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TeleprompterView;
